import { useMemo } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'

import { ProductLotTransferForm } from './new'
import type { TransferFormData } from './new'
import type { CreateTransferNoteRequest } from '@/lib/api/product-lot-api'
import type { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { useProductLotTransferNoteQuery } from '@/hooks/use-product-lot-query'
import { useUpdateProductLotTransferNote } from '@/hooks/use-product-lot-mutation'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sales-warehouse/transfer-notes/$id_/edit',
)({
    component: EditProductLotTransferNotePage,
})

export function EditProductLotTransferNotePage() {
    const { id } = Route.useParams()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { data, isLoading } = useProductLotTransferNoteQuery(id)
    const note = data?.data
    const update = useUpdateProductLotTransferNote()

    const defaultValues = useMemo<TransferFormData | null>(() => {
        if (!note) return null
        return {
            notes: note.notes ?? '',
            items: (note.items ?? []).map((it) => ({
                source_product_lot_id: it.source_product_lot_id,
                quantity: it.quantity,
                dest_position_id: it.dest_position_id,
            })),
        }
    }, [note])

    if (isLoading) return <div className="p-6">{m.sl_detail_loading()}</div>
    if (!note || !defaultValues)
        return <div className="p-6">{m.sl_detail_note_not_found()}</div>

    const isCreator = !!user?.id && note.created_by_id === user.id
    const editable = note.status === 'pending' && isCreator

    if (!editable) {
        return (
            <div className="space-y-4 p-6">
                <Button asChild variant="ghost" className="-ml-2">
                    <Link
                        to="/sales-warehouse/transfer-notes/$id"
                        params={{ id }}
                    >
                        <ArrowLeft className="size-4 mr-1" />
                        {m.sl_lot_back()}
                    </Link>
                </Button>
                <p className="text-muted-foreground">
                    {m.pl_pnk_edit_not_allowed()}
                </p>
            </div>
        )
    }

    const backToDetail = () =>
        navigate({
            to: '/sales-warehouse/transfer-notes/$id',
            params: { id },
            replace: true,
        })

    const handleSubmit = async (
        req: CreateTransferNoteRequest,
        workflow: ReturnType<typeof useWorkflowTracking>,
    ) => {
        await workflow.run(
            () => update.mutateAsync({ id, req }),
            (res) => res.data?.id,
        )
        void backToDetail()
    }

    return (
        <ProductLotTransferForm
            mode="edit"
            attemptKey={id}
            title={m.pdc_edit_title()}
            submitLabel={m.pl_pnk_edit_submit()}
            isPending={update.isPending}
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
            onCancel={() => void backToDetail()}
        />
    )
}
